import css from "@css/modules/Listing.module.scss"
import { StartRating } from "@components/StartRating"
import { Listing, TopListing } from "@t/Listing"
import { useMemo } from "react"
import { getPointMinMax } from "@utils/listingUrl"
import DealScoreImg from "@public/images/dealScoreLogo.png"
import Image from "next/image"
import { DealScoreTable } from "@components/Listings/DealScore/DealScoreTable"
import { getPointStatus, getPointsText } from "@components/Listings/DealScore/utils"
import { DealScoreDialog } from "@components/Listings/DealScoreDialog"
import { ownNotActiveStatuses } from "@utils/const"
import cn from "classnames"
import Link from "next/link"
import { getQueryFromFilters } from "@components/Listings/ListingsFilter/store"

type Props = {
    listing: Listing
    topListings: TopListing[]
    total: number
}

export function DealScore({ listing, topListings, total = 0 }: Props) {
    const { pointsMin, pointsMax } = useMemo(() => getPointMinMax(listing), [listing])

    const search = useMemo(() => {
        return getQueryFromFilters({
            resort: { short_name_in: [listing?.resort?.short_name] },
            points_on_contract_gte: pointsMin,
            points_on_contract_lte: pointsMax
        })
    }, [listing?.resort?.short_name, pointsMin, pointsMax])

    const notActive = useMemo(() => {
        return ownNotActiveStatuses.some((i) => listing.listing_status.includes(i))
    }, [listing.listing_status])
    return (
        <div className={cn(css.dealScore, notActive && css.notActive)}>
            <div className={css.dealScoreLeft}>
                <Image quality={90} src={DealScoreImg} alt="Deal score" width={192} height={37} />
                {!listing.deal_score ? (
                    <div className={css.notActiveListing}>This listings do not have a DealScore.</div>
                ) : (
                    <>
                        <StartRating rating={listing.stars || 1} size={26} gap={9.75} className={css.starRating} />
                        <div className={css.dealScoreText}>
                            Compared to other {listing?.resort?.short_name} listings with <Link href={`/listings?${search}`}>{getPointsText(pointsMin, pointsMax)}.</Link>
                            <div className={css.dealScoreStats}>
                                <div className={css.dealScoreStatsItem}>
                                    <span>DealScore Calculation: </span>
                                    Ranked #{listing.rank}
                                </div>
                                <div className={css.dealScoreStatsItem}>
                                    <span>Price-Per-Point:</span>
                                    Ranked #{listing.price_per_point_rank}
                                </div>
                                <div className={css.dealScoreStatsItem}>
                                    <span>Contract Point Status:</span>
                                    {getPointStatus(listing)}
                                </div>
                                {listing.resort.id === 11 && ( // old key west
                                    <div className={css.dealScoreStatsItem}>
                                        <span>Special Considerations:</span>
                                        {listing.deed_exp} Deed
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className={css.dealScoreRight}>
                <DealScoreTable notActive={notActive} listing={listing} topListings={topListings} title={`Similar ${listing.resort.short_name} Listings`} />
            </div>
            {notActive ? (
                <div className={css.notActiveExplain}>
                    We calculated the deal score before this listing was <strong>sold</strong>.
                </div>
            ) : (
                <DealScoreDialog className={css.dealScoreDialogWrapper} listing={listing} topListings={topListings} total={total} />
            )}
        </div>
    )
}
